import { cn } from '@ai-tutor/ui';
import { useId, useMemo, useState, type ReactNode } from 'react';

import { BillingIntervalToggle } from '@/components/features/billing/forms/BillingIntervalToggle';
import type { Plan } from '@/lib/api-client';
import {
  annualPromoSavingsPct,
  groupPlans,
  paidPlanFor,
  planPriceLines,
  type BillingInterval,
} from '@/lib/billing';

import { PlanOption } from './PlanOption';

/**
 * The one plan chooser — onboarding's pricing step, the Settings Upgrade
 * modal and the Change-plan dialog all render this.
 *
 * There used to be three, and they disagreed: onboarding toggled Free vs Plus
 * by interval, Upgrade drew a not-selectable card beside the toggle, and
 * Change plan listed every catalog row (so "Plus" twice) with no toggle at
 * all. This is onboarding's design, which is the one the other two were
 * meant to match: a Monthly / Yearly toggle (only when the catalog has both),
 * then Free beside the Plus plan for that interval.
 *
 * Purely presentational. The parent owns the catalog fetch, the selection and
 * what the toggle does to it — the callers want different things there (the
 * Upgrade modal moves its selection with the toggle; Change plan must never
 * pre-select a change the user did not click).
 *
 * - Pass `onSelect` and the cards become a radio group; leave it out and they
 *   are plain cards whose actions come from `renderFooter` (onboarding).
 * - `currentPlanId` badges that card "Current plan". Free is always on screen,
 *   so its badge always is; a paid plan's shows while the toggle is on its
 *   interval.
 */
export interface PlanPickerProps {
  /** The catalog (`GET /payments/plans`). */
  plans: Plan[];
  /** Controlled interval. Omit to let the picker hold it. */
  interval?: BillingInterval;
  /** Uncontrolled starting interval. Defaults to yearly, where the promo is. */
  initialInterval?: BillingInterval;
  onIntervalChange?: (interval: BillingInterval) => void;
  selectedPlanId?: string | null;
  /** Makes the cards a radio group. */
  onSelect?: (plan: Plan) => void;
  currentPlanId?: string | null;
  /** Mark the paid plan "Popular". Default true. */
  recommended?: boolean;
  /** Per-card actions, rendered below the features. */
  renderFooter?: (plan: Plan) => ReactNode;
  /** `data-testid` per card. Default `plan-option-<id>`. */
  cardTestId?: (plan: Plan) => string;
  /** The radio group's accessible name. */
  label?: string;
  className?: string;
}

const defaultTestId = (plan: Plan) => `plan-option-${plan.id}`;

/** The interval actually shown: the chosen one, or the only one there is. */
function shownInterval(
  requested: BillingInterval,
  hasMonthly: boolean,
  hasAnnual: boolean
): BillingInterval {
  if (hasMonthly && hasAnnual) return requested;
  if (hasMonthly) return 'month';
  if (hasAnnual) return 'year';
  return requested;
}

export function PlanPicker({
  plans,
  interval: controlledInterval,
  initialInterval = 'year',
  onIntervalChange,
  selectedPlanId = null,
  onSelect,
  currentPlanId = null,
  recommended = true,
  renderFooter,
  cardTestId = defaultTestId,
  label = 'Choose a plan',
  className,
}: PlanPickerProps) {
  const groupName = useId();
  const grouped = useMemo(() => groupPlans(plans), [plans]);
  const [ownInterval, setOwnInterval] = useState(initialInterval);
  const hasBoth = !!grouped.monthly && !!grouped.annual;
  const interval = shownInterval(
    controlledInterval ?? ownInterval,
    !!grouped.monthly,
    !!grouped.annual
  );

  const handleInterval = (next: BillingInterval) => {
    setOwnInterval(next);
    onIntervalChange?.(next);
  };

  const cards = [grouped.free, paidPlanFor(grouped, interval)].filter(
    (p): p is Plan => !!p
  );

  return (
    <div className={cn('space-y-6', className)}>
      {hasBoth && (
        <div className='flex justify-center'>
          <BillingIntervalToggle
            value={interval}
            onChange={handleInterval}
            savingsPct={annualPromoSavingsPct(grouped.annual, grouped.monthly)}
          />
        </div>
      )}
      <div
        role={onSelect ? 'radiogroup' : undefined}
        aria-label={onSelect ? label : undefined}
        className='grid gap-4 md:grid-cols-2'
      >
        {cards.map(plan => (
          <PlanOption
            key={plan.id}
            plan={plan}
            lines={planPriceLines(plan, grouped)}
            isCurrent={plan.id === currentPlanId}
            recommended={recommended && plan !== grouped.free}
            radio={
              onSelect
                ? {
                    name: groupName,
                    checked: plan.id === selectedPlanId,
                    onChange: () => onSelect(plan),
                  }
                : undefined
            }
            footer={renderFooter?.(plan)}
            data-testid={cardTestId(plan)}
          />
        ))}
      </div>
    </div>
  );
}
